import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Clock, Key, Lock } from "@phosphor-icons/react";
import { attributeActor, type TenantRoster } from "../../lib/actor";
import {
  SCOPE_LABEL,
  STATUS_LABEL,
  TYPE_LABEL,
  isClosedStatus,
  useAddRequestComment,
  useReopenRequest,
  useRequestThread,
  type ClientRequest,
  type RequestStatus
} from "../../data/requestQueries";
import {
  attachmentRefusal,
  useAttachmentDownload,
  useComposeWithAttachments,
  useRequestAttachments,
  type AttachmentUploadFailure
} from "../../data/attachmentQueries";
import { AttachmentList } from "../../components/AttachmentList";
import { draftKey, useSessionDraft } from "../../lib/drafts";
import { ClientHead } from "../../ui/ClientHead";
import { Panel } from "../../ui/Panel";
import { BoxState } from "../../ui/BoxState";
import { Callout } from "../../ui/Callout";
import { Composer } from "../../ui/Composer";
import { monthWord } from "../../lib/requestMonth";
import { OverCapTag, StatusWord } from "./requestWords";
import { dateTime } from "./requestTime";
import { useClientPath } from "../../data/readSource";

/**
 * `/requests/:number`: one request on its own page (`prototype/kund/pages.js` `requestPage`,
 * approved 2026-09-24). No list beside it, so the number, the status and the service are each said
 * once, in the head box, and the updated time the old detail repeated from its list row is gone: the
 * timeline below carries every moment the request has had.
 *
 * Top to bottom: the way back and the title; a box with the number, the status and the facts; the
 * one thing that asks something of the client, Petrovic's question, when there is one; what the
 * client wrote; and the timeline with the answer box at its foot.
 *
 * Build round A2, 2026-10-08, adds three things from the monthly cap and the split. A request waiting
 * for Petrovic's choice over the cap says so beside its status and in an info box that tells the
 * client what happens next. A request moved to the next month says which month it counts in; Extra
 * and Eget pris need no row of their own, because the classification already says them. And a request
 * split in two links both ways, from the part to the request it came from and from that request to
 * each part, so the client can follow what became of what they wrote.
 */

/** The requests a split connects this one to, found in the client's own list by the route. */
export interface SplitLinks {
  /** The request this one was split from, or null when it was not, or is not in the list. */
  from: { requestNumber: number } | null;
  /** The parts this one was split into, by number. */
  into: number[];
}

interface TimelineEntry {
  key: string;
  at: string;
  kind: "comment" | "event";
  body: string;
  authorUserId: string | null;
  /**
   * The comment this entry is, when it is one.
   *
   * `public.attachments` has no `request_id`. A file reaches a request only through
   * `request_comment_id`, so the timeline entry has to carry the id it was built from in order for
   * anything to hang under it. Status events carry null, and nothing can be attached to one.
   */
  commentId: string | null;
}

/**
 * Who a comment is shown as having come from.
 *
 * Four answers, and each earlier count was wrong for a reason worth keeping written down.
 *
 * Two answers were wrong because `request_comments.author_user_id` is `uuid null ... on delete set
 * null`: a comment written by a colleague whose account was later deleted survives with a null
 * author, and labelling every other account **Petrovic** attributed a former colleague's words to
 * the client's supplier. A null author gets `Okänd avsändare`, which is the only sentence the row
 * supports.
 *
 * Three answers were wrong because every remaining account fell to `Petrovic`, which is true only
 * while a tenant has one person in it. `public.memberships` is built for more, and the moment there
 * are two, each of them reads the other's comments as though Petrovic had written them.
 *
 * The fourth answer is the roster. `public.contacts.is_authorised_approver` marks the person whose
 * approval binds the client, and a thread that cannot name the writer cannot show that the approver
 * was the one who agreed to the change.
 */
function commentAuthor(
  authorUserId: string | null,
  currentUserId: string | null,
  displayName: string | null,
  roster: TenantRoster
): { name: string; initial: string; petrovic: boolean } {
  const actor = attributeActor(authorUserId, currentUserId, displayName, roster);

  return { name: actor.name, initial: actor.initial, petrovic: actor.kind === "petrovic" };
}

/** A link to a request that is not among this tenant's requests: gone, mistyped or someone else's. */
export function RequestNotFound({ number }: { number: string }) {
  const clientPath = useClientPath();
  return (
    <div className="k-page k-request">
      <ClientHead title={`Ärende ${number}`} titleId="request-title" back={{ to: clientPath("/requests"), label: "Ärenden" }} />
      <Panel>
        <BoxState kind="empty" title="Ärendet finns inte" body="Gå tillbaka till listan och välj ett ärende där." />
      </Panel>
    </div>
  );
}

export function RequestPage({
  request,
  tenantId,
  serviceName,
  writable,
  currentUserId,
  displayName,
  roster,
  split = { from: null, into: [] }
}: {
  request: ClientRequest;
  tenantId: string;
  serviceName: string;
  writable: boolean;
  currentUserId: string | null;
  displayName: string | null;
  /**
   * Resolved before this component is reached. The route holds its loading state until the map
   * exists, so nothing here has to decide what to print while the answer is still on its way.
   */
  roster: TenantRoster;
  split?: SplitLinks;
}) {
  const clientPath = useClientPath();
  const thread = useRequestThread(request.id);
  const comment = useAddRequestComment();
  const reopen = useReopenRequest();
  const compose = useComposeWithAttachments();
  const download = useAttachmentDownload();
  /*
    One draft per request, since 2026-09-22, keyed by the request's id so a half-written answer on
    REQ-41 never appears in the box on REQ-40. The route remounts this page per request by its `key`,
    which is exactly what used to throw the text away. Cleared only after the comment or the
    reopening has landed; `app/lib/drafts.ts` says why the tab is the right lifetime.
  */
  const [draft, setDraft, clearDraft] = useSessionDraft(
    draftKey(currentUserId, tenantId, `request-comment:${request.id}`),
    ""
  );
  const [files, setFiles] = useState<File[]>([]);
  const [sendFailures, setSendFailures] = useState<AttachmentUploadFailure[]>([]);
  const idempotencyKey = useRef(crypto.randomUUID());

  const waiting = request.status === "waiting_for_client";
  /*
   * Two words for two questions since build round A1 (2026-10-08). `completed` decides the reopening,
   * which only ever applies to Klart: `reopen_request` refuses anything else. `closed` decides which
   * list the request belongs to, and Avböjt is under Klara with Klart. A declined request takes no
   * comment: `add_request_comment` refuses it (`PORTAL_REQUEST_IS_FINAL`), because Inkorg counts it as
   * closed and the comment would reach nobody. A question about it goes to the chat.
   */
  const completed = request.status === "completed";
  const closed = isClosedStatus(request.status);
  const declined = request.status === "declined";
  // Build round A2: the month a request moved to with "Nästa månad", read from its digits.
  const countsIn = request.over_cap_choice === "next_month" ? monthWord(request.counts_in_month) : null;

  /**
   * The comment ids this thread currently holds, which is the only handle a request has on its files.
   *
   * Recomputed from the thread rather than kept in state, so a comment added a moment ago is in the
   * list as soon as the thread refetches, and the attachment query's key changes with it.
   */
  const commentIds = useMemo(
    () => (thread.data?.comments ?? []).map((row) => row.id),
    [thread.data]
  );

  const attachments = useRequestAttachments(request.id, commentIds);
  const filesByComment = attachments.data;

  const timeline: TimelineEntry[] = useMemo(() => {
    const comments = (thread.data?.comments ?? []).map((row) => ({
      key: `c-${row.id}`,
      at: row.created_at,
      kind: "comment" as const,
      body: row.body ?? "",
      authorUserId: row.author_user_id,
      commentId: row.id
    }));

    // A `scope_decided` event repeats the request's current status, so rendering it as a status
    // change would show a change that never happened. It is left out of the timeline; the
    // classification itself is in the head box above.
    const events = (thread.data?.events ?? [])
      .filter((row) => row.event_kind !== "scope_decided")
      .map((row) => ({
        key: `e-${row.id}`,
        at: row.occurred_at,
        kind: "event" as const,
        body: STATUS_LABEL[row.to_status as RequestStatus] ?? row.to_status,
        authorUserId: row.actor_user_id,
        commentId: null
      }));

    return [...comments, ...events].sort((left, right) => left.at.localeCompare(right.at));
  }, [thread.data]);

  /**
   * A comment or a reopening, with text, files, or both.
   *
   * Without a file it goes straight to its own gateway. With one it goes to `/api/files/compose`,
   * which writes the comment or the reopening and admits its files in one transaction
   * (`supabase/migrations/20260913000003_…`). A file whose bytes fail after the comment exists is
   * reported and never rolled back, and stays selected so a second send carries only what did not
   * arrive.
   */
  async function onComment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = draft.trim();
    if (!body && files.length === 0) {
      return;
    }

    if (files.length > 0) {
      try {
        const outcome = await compose.mutateAsync({
          tenantId,
          parent: completed
            ? { kind: "request_reopen", requestId: request.id, body: body || null }
            : {
                kind: "request_comment",
                requestId: request.id,
                body: body || null,
                idempotencyKey: idempotencyKey.current
              },
          files
        });

        clearDraft();
        idempotencyKey.current = crypto.randomUUID();
        setFiles(outcome.failed.map((entry) => entry.file));
        setSendFailures(outcome.failed);
      } catch {
        // Nothing was written. Rendered from the mutation's own error below.
      }
      return;
    }

    try {
      if (completed) {
        // A reply to a finished request reopens it, which is the only way a client changes a
        // status. The gateway appends a `reopened` event and leaves the completion in place.
        await reopen.mutateAsync({ tenantId, requestId: request.id, body });
      } else {
        await comment.mutateAsync({
          tenantId,
          requestId: request.id,
          body,
          idempotencyKey: idempotencyKey.current
        });
      }
      clearDraft();
      idempotencyKey.current = crypto.randomUUID();
      setSendFailures([]);
    } catch {
      // Rendered from the mutation's own error below. The files stay selected, because nothing was
      // written and the next attempt is the same attempt.
    }
  }

  const pending = comment.isPending || reopen.isPending || compose.isPending;
  const failure = comment.error ?? reopen.error ?? compose.error;
  // A file the browser already knows will be refused must not be sent. The composer says why beside
  // each one; this is only what stops the form.
  const refusedFile = files.some((file) => attachmentRefusal(file) !== null);
  const title = request.title ?? "Utan rubrik";

  return (
    <div className="k-page k-request" data-testid="request-detail">
      <ClientHead
        title={title}
        titleId="request-title"
        // Back to the list that holds this request: a finished one is under Klara.
        back={{ to: clientPath(closed ? "/requests?status=klara" : "/requests"), label: "Ärenden" }}
      />

      <section className="panel k-req-head" data-testid="request-head">
        <div className="panel-body">
          <div className="k-req-top">
            <span className="k-req-number">REQ-{request.request_number}</span>
            <span className="k-req-marks">
              {request.over_cap ? <OverCapTag testId="request-page-over-cap" /> : null}
              <StatusWord status={request.status} testId="request-status" />
            </span>
          </div>
          <dl className="kv">
            <div>
              <dt>Tjänst</dt>
              <dd>{serviceName}</dd>
            </div>
            <div>
              <dt>Typ</dt>
              <dd>{TYPE_LABEL[request.request_type] ?? request.request_type}</dd>
            </div>
            {/*
              The row appears once Petrovic has classified the request (docs/polish.md P-9,
              2026-09-24). Before that, "Ej klassificerad" told a client nothing they could act on.
              The admin board still shows the unclassified state, because there it is work to do.
              It is the only price signal a client sees.
            */}
            {request.scope_result ? (
              <div data-testid="request-classification">
                <dt>Klassificering</dt>
                <dd>{SCOPE_LABEL[request.scope_result] ?? request.scope_result}</dd>
              </div>
            ) : null}
            {/*
              Build round A1, 2026-10-08: a request Petrovic filed for the client says so, since the
              client did not write it and would otherwise wonder who did. A request the client filed
              needs no row: that is every other request.
            */}
            {request.origin === "petrovic" ? (
              <div data-testid="request-origin">
                <dt>Skapat av</dt>
                <dd>Petrovic Systems</dd>
              </div>
            ) : null}
            {/*
              Build round A2, 2026-10-08: Petrovic moved the request to the next month's small
              changes. The month is the client's to know, because it is the month's count it is in.
            */}
            {countsIn ? (
              <div data-testid="request-counts-in">
                <dt>Räknas i</dt>
                <dd>{countsIn}</dd>
              </div>
            ) : null}
            {split.from ? (
              <div data-testid="request-split-from">
                <dt>Del av</dt>
                <dd>
                  <Link to={clientPath(`/requests/${split.from.requestNumber}`)}>REQ-{split.from.requestNumber}</Link>
                </dd>
              </div>
            ) : null}
            {split.into.length > 0 ? (
              <div data-testid="request-split-into">
                <dt>Uppdelat i</dt>
                <dd>
                  {split.into.map((number, index) => (
                    <span key={number}>
                      {index > 0 ? ", " : null}
                      <Link to={clientPath(`/requests/${number}`)}>REQ-{number}</Link>
                    </span>
                  ))}
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
      </section>

      {/*
        Build round A1, 2026-10-08: Avböjt and why, in Petrovic's words as written. An info box
        rather than a warning, because it asks nothing of the client; the reason is plain text and
        rendered as text.
      */}
      {declined ? (
        <Callout tone="info" testId="request-declined">
          <h2>Petrovic Systems har avböjt ärendet</h2>
          {request.decline_reason ? <p>{request.decline_reason}</p> : null}
        </Callout>
      ) : null}

      {/*
        Build round A2, 2026-10-08: the request was received when the month's small changes were
        already used. Info, not a warning: the client has done nothing wrong and is asked nothing, and
        the answer arrives on this page, as Extra, Kräver separat pris or the month it counts in.
      */}
      {request.over_cap ? (
        <Callout tone="info" testId="request-over-cap-callout">
          <h2>Över månadens tak</h2>
          <p>
            Månadens små ändringar är redan gjorda. Petrovic Systems väljer om den här görs utan kostnad, räknas
            nästa månad eller får ett eget pris, och ni ser beskedet här.
          </p>
        </Callout>
      ) : null}

      {waiting && request.waiting_for_client_note ? (
        <Callout tone="warn" testId="request-waiting">
          <h2>Petrovic Systems väntar på ert besked</h2>
          <p>{request.waiting_for_client_note}</p>
        </Callout>
      ) : null}

      <Panel title="Beskrivning" titleId="request-description-title" className="k-req-description">
        {/*
          Three answers, and the failed read used to borrow the third one's sentence.
          `thread.data?.description ?? "Ingen beskrivning lämnades."` covered loading and nothing
          else, so a refused read printed a statement about what the customer wrote while the
          timeline below said the history could not be fetched. The failure is rendered the same way
          the timeline renders its own, from the same query, because one refetch fixes both.
        */}
        {thread.isLoading ? (
          <BoxState kind="loading" compact title="Hämtar ärendet" />
        ) : thread.isError ? (
          <BoxState
            kind="error"
            compact
            title="Beskrivningen kunde inte hämtas"
            body="Ärendet finns kvar. Försök igen."
            onRetry={() => void thread.refetch()}
          />
        ) : (
          <p className="k-req-text">{thread.data?.description ?? "Ingen beskrivning lämnades."}</p>
        )}
      </Panel>

      <Panel title="Händelser och kommentarer" titleId="timeline-title" flush className="k-thread-box">
        {thread.isError ? (
          <BoxState
            kind="error"
            compact
            title="Historiken kunde inte hämtas"
            body="Ärendet finns kvar. Försök igen."
            onRetry={() => void thread.refetch()}
          />
        ) : timeline.length > 0 ? (
          <ol className="k-thread">
            {timeline.map((entry) => {
              if (entry.kind === "event") {
                // A status change is a quiet line: it happened, and it asks nothing of anyone.
                return (
                  <li className="k-event" key={entry.key}>
                    <Clock size={16} aria-hidden="true" />
                    <span>Status ändrades till {entry.body}</span>
                    <time dateTime={entry.at}>{dateTime.format(new Date(entry.at))}</time>
                  </li>
                );
              }

              const author = commentAuthor(entry.authorUserId, currentUserId, displayName, roster);

              return (
                <li className="k-comment" key={entry.key}>
                  <span className={`bubble-avatar${author.petrovic ? " is-petrovic" : ""}`} aria-hidden="true">
                    {author.initial}
                  </span>
                  <div className="k-comment-main">
                    <p className="k-comment-meta">
                      <strong>{author.name}</strong>
                      <time dateTime={entry.at}>{dateTime.format(new Date(entry.at))}</time>
                    </p>
                    {entry.body ? <p className="k-comment-body">{entry.body}</p> : null}
                    {entry.commentId ? (
                      <AttachmentList
                        tenantId={tenantId}
                        attachments={filesByComment?.get(entry.commentId) ?? []}
                        label={`Bilagor från ${author.name}, ${dateTime.format(new Date(entry.at))}`}
                        download={download}
                      />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        ) : null}

        {/*
          A failed attachment read is said once, under the timeline, and never allowed to hide the
          comments. The two are separate queries and the comments are intact; what is missing is the
          list of files hanging off them, and a client who cannot see a file that exists should be
          told rather than shown a thread that looks complete.
        */}
        {attachments.isError ? (
          <BoxState
            kind="error"
            compact
            title="Bilagorna kunde inte hämtas"
            body="Kommentarerna visas, men filer som hör till dem syns inte just nu."
            onRetry={() => void attachments.refetch()}
          />
        ) : null}

        {/*
          The shared one-row composer. On a finished request the same box reopens it with a message,
          and a reopening may be a file too, since 2026-09-13: "the site is still broken" is most
          often a screenshot. In Läsläge the whole row is one locked line, said once (FEATURE-MAP.md,
          "Numbers shown more than once", item 11).

          A declined request is final and takes no reply (build round A1, 2026-10-08). The row says so
          in the composer's own locked form and points to the chat, where Petrovic reads it.
        */}
        {declined ? (
          <div className="k-composer is-locked" role="status" data-testid="request-final">
            <Lock size={18} aria-hidden="true" />
            <span>
              Ärendet är avböjt och tar inte emot fler svar. Frågor om det skriver ni i{" "}
              <Link to={clientPath("/chat")}>chatten</Link>.
            </span>
          </div>
        ) : (
          <Composer
            id="request-comment"
            label={completed ? "Öppna ärendet igen med ett meddelande" : "Skriv en kommentar"}
            placeholder={completed ? "Öppna ärendet igen med ett meddelande" : "Skriv ert svar"}
            value={draft}
            onChange={setDraft}
            onSubmit={onComment}
            maxLength={5000}
            pending={pending}
            canSubmit={(draft.trim().length > 0 || files.length > 0) && !refusedFile}
            locked={!writable}
            submitLabel={completed ? "Öppna igen" : "Skicka"}
            pendingLabel={completed ? "Öppnar" : "Skickar"}
            attachments={{
              inputId: `request-attachment-${request.id}`,
              files,
              onChange: setFiles,
              sendFailures
            }}
            testId="request-composer"
            foot={
              <Link className="k-secret-link" to="/secrets">
                <Key size={14} aria-hidden="true" />
                Lösenord eller nyckel? Skicka en hemlighet
              </Link>
            }
            errors={
              failure ? (
                <p className="form-error" role="alert" data-testid="comment-error">
                  {failure.message}
                </p>
              ) : null
            }
          />
        )}
      </Panel>
    </div>
  );
}
